import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Calendar from 'react-calendar';
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
    Avatar,
    Divider,
    IconButton,
    Stack,
    Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Chip,
    InputAdornment
} from '@mui/material';
import axiosInstance from '../../services/axiosInstance';
import { format } from 'date-fns';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import VideocamIcon from '@mui/icons-material/Videocam';
import PublicIcon from '@mui/icons-material/Public';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import apiEndPointsConfig from '../../services/apiEndPoints';

import 'react-calendar/dist/Calendar.css';
import styles from './BookingPage.module.css'; // Import the new CSS file as module

// Constants
const CALENDLY_BLUE = '#0069ff';
const TEXT_PRIMARY = '#1a1a1a';
const TEXT_SECONDARY = '#666666';

const BookingPage = ({ showDashboard = false }) => {
    const [searchParams] = useSearchParams();
    // In a real scenario, use providerId from props if in dashboard, or param if public
    const providerId = searchParams.get('providerId') || localStorage.getItem('userId');

    const [providerData, setProviderData] = useState(null);
    const [date, setDate] = useState(new Date()); // Initialize with today's date
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [openForm, setOpenForm] = useState(false);

    // Layout State: 0 = Just Month, 1 = Month + Slots
    const [showSlots, setShowSlots] = useState(false);

    // Blocking Mode (Dashboard Only)
    const [isBlockingMode, setIsBlockingMode] = useState(false);
    const [selectedSlotsForBlock, setSelectedSlotsForBlock] = useState([]);
    const [blocking, setBlocking] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', notes: '',
        mode: 'online',
        meetingLink: '',
        location: '',
        guests: [],
        guestNameInput: '',
        guestEmailInput: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleAddGuest = () => {
        if (!formData.guestEmailInput || !/\S+@\S+\.\S+/.test(formData.guestEmailInput)) {
            setError('Please enter a valid email for guest');
            return;
        }
        if (!formData.guestNameInput) {
            setError('Please enter guest name');
            return;
        }

        const alreadyExists = formData.guests.some(g => g.email === formData.guestEmailInput);
        if (alreadyExists) {
            setFormData({ ...formData, guestEmailInput: '', guestNameInput: '' });
            setError('');
            return;
        }

        setError('');
        setFormData({
            ...formData,
            guests: [...formData.guests, { name: formData.guestNameInput, email: formData.guestEmailInput }],
            guestNameInput: '',
            guestEmailInput: ''
        });
    };

    const handleRemoveGuest = (emailToRemove) => {
        setFormData({
            ...formData,
            guests: formData.guests.filter(g => g.email !== emailToRemove)
        });
    };

    // Appointments state (Dashboard Feature)
    const [appointments, setAppointments] = useState([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);

    useEffect(() => {
        if (providerId) {
            fetchProviderDetails();
        }
    }, [providerId]);

    const [userNotFound, setUserNotFound] = useState(false);

    const fetchProviderDetails = async () => {
        try {
            const res = await axiosInstance.get(`${apiEndPointsConfig.getUserCompanyDetails}/${providerId}`);
            if (res.data?.user) {
                setProviderData({
                    name: res.data.user.name,
                    avatar: res.data.user.profileImage,
                    title: res.data.user.companyName || 'Strategy Session',
                    duration: '30 min'
                });
            }
        } catch (error) {
            console.error("Error fetching provider details:", error);
            if (error.response && error.response.status === 404) {
                setUserNotFound(true);
            } else {
                // Fallback for other errors (network, 500, etc)
                setProviderData({
                    name: 'Provider',
                    title: 'Strategy Session',
                    duration: '30 min'
                });
            }
        }
    };

    useEffect(() => {
        if (providerId && date) {
            fetchSlots();
            setShowSlots(true);
        }
    }, [date, providerId]);

    // Fetch appointments if showing dashboard
    useEffect(() => {
        if (showDashboard && providerId) {
            fetchAppointments();
        }
    }, [showDashboard, providerId]);

    const fetchAppointments = async () => {
        if (!showDashboard) return; // Prevent fetch if not in dashboard mode
        setLoadingAppointments(true);
        try {
            const res = await axiosInstance.get(`/appointments`, {
                params: { userId: providerId }
            });
            if (res.data.success) {
                // Filter: show appointments from today onwards
                const startOfDay = new Date();
                startOfDay.setHours(0, 0, 0, 0);

                const upcoming = res.data.appointments
                    .filter(appt => new Date(appt.startTime) >= startOfDay && appt.status !== 'cancelled')
                    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

                setAppointments(upcoming);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
        } finally {
            setLoadingAppointments(false);
        }
    };

    const handleCancel = async (appointmentId) => {
        if (!window.confirm("Are you sure you want to cancel this meeting?")) return;
        try {
            await axiosInstance.put(`/appointments/status`, { appointmentId, status: 'cancelled' });
            fetchAppointments(); // Refresh list
            setSnackbar({ open: true, message: 'Meeting cancelled successfully', severity: 'success' });
        } catch (error) {
            console.error("Error cancelling appointment:", error);
            setSnackbar({ open: true, message: 'Failed to cancel meeting', severity: 'error' });
        }
    };

    const handleRemind = async (appointmentId) => {
        try {
            const res = await axiosInstance.post(`/appointments/remind`, { appointmentId });
            if (res.data.success) {
                setSnackbar({
                    open: true,
                    message: 'Reminder sent successfully to client!',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error("Error sending reminder:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to send reminder',
                severity: 'error'
            });
        }
    };

    const fetchSlots = async () => {
        setLoadingSlots(false);
        setSlots([]); // clear previous
        try {
            const dateStr = format(date, "yyyy-MM-dd");
            const res = await axiosInstance.get(`/appointments/slots`, {
                params: {
                    userId: providerId,
                    date: dateStr,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }
            });
            if (res.data.success) {
                setSlots(res.data.slots);
            }
        } catch (err) {
            console.error(err);
            // Mock slots if API fails for demo
            setTimeout(() => {
                setSlots([
                    { formattedStart: '09:00', start: new Date(), end: new Date() },
                    { formattedStart: '09:30', start: new Date(), end: new Date() },
                    { formattedStart: '10:00', start: new Date(), end: new Date() },
                    { formattedStart: '10:30', start: new Date(), end: new Date() },
                    { formattedStart: '11:00', start: new Date(), end: new Date() },
                    { formattedStart: '14:00', start: new Date(), end: new Date() },
                    { formattedStart: '15:30', start: new Date(), end: new Date() },
                ]);
                setLoadingSlots(false);
            }, 600);
        }
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
    };

    const handleSlotClick = (slot) => {
        if (isBlockingMode) {
            const alreadySelected = selectedSlotsForBlock.some(s => s.formattedStart === slot.formattedStart);
            if (alreadySelected) {
                setSelectedSlotsForBlock(selectedSlotsForBlock.filter(s => s.formattedStart !== slot.formattedStart));
            } else {
                setSelectedSlotsForBlock([...selectedSlotsForBlock, slot]);
            }
        } else {
            setSelectedSlot(slot);
            setOpenForm(true);
        }
    };

    const handleBlockSlots = async () => {
        if (selectedSlotsForBlock.length === 0) return;
        setBlocking(true);
        try {
            const res = await axiosInstance.post(`/appointments/block`, {
                userId: providerId,
                slots: selectedSlotsForBlock
            });
            if (res.data.success) {
                setSnackbar({ open: true, message: 'Slots blocked successfully', severity: 'success' });
                setSelectedSlotsForBlock([]);
                setIsBlockingMode(false);
                fetchSlots(); // Refresh
            }
        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Failed to block slots', severity: 'error' });
        } finally {
            setBlocking(false);
        }
    };

    const handleBook = async () => {
        setSubmitting(true);
        setError('');
        try {
            await axiosInstance.post(`/appointments/book`, {
                providerId,
                startTime: selectedSlot.start,
                endTime: selectedSlot.end,
                clientDetails: formData,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            setSuccess(true);
            setOpenForm(false);
            // Reset form data
            setFormData({
                name: '', email: '', phone: '', notes: '',
                mode: 'online',
                meetingLink: '',
                location: '',
                guests: [],
                guestNameInput: '',
                guestEmailInput: ''
            });
            // If in dashboard, refresh list
            if (showDashboard) fetchAppointments();
        } catch (err) {
            setError(err.response?.data?.message || 'Booking failed');
        } finally {
            setSubmitting(false);
        }
    };

    const handleShare = () => {
        const url = `${window.location.origin}/book?providerId=${providerId}`;
        navigator.clipboard.writeText(url).then(() => {
            setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
        });
    };

    // User Not Found View
    if (userNotFound) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                <Box textAlign="center" maxWidth={400} p={3}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: TEXT_PRIMARY }}>User Not Found</Typography>
                    <Typography color="text.secondary" paragraph>
                        The booking link you are trying to access is invalid or the user does not exist.
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Success View
    if (success) {
        return (
            <Box sx={{ minHeight: showDashboard ? 'auto' : '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
                <Box textAlign="center" maxWidth={400} p={3}>
                    <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#d1fae5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: TEXT_PRIMARY }}>Confirmed</Typography>
                    <Typography color="text.secondary" paragraph>
                        You are scheduled with {providerData?.name}.<br /> A calendar invitation has been sent to your email address.
                    </Typography>
                    <Divider sx={{ my: 3 }} />
                    <Button variant="text" onClick={() => setSuccess(false)} sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                        Schedule another meeting
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={styles["booking-container"]}>

            {/* Main Booking Card */}
            <div className={`${styles["booking-card"]} ${showDashboard ? styles["dashboard-mode"] : ''}`}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%',
                    minHeight: 500,
                    bgcolor: '#fff',
                }}>
                    {/* Left Pane - Provider Details */}
                    <Box sx={{
                        width: { xs: '100%', md: '30%' },
                        minWidth: '300px',
                        p: { xs: 3, md: 4 },
                        borderRight: { md: '1px solid #eaeaea' },
                        borderBottom: { xs: '1px solid #eaeaea', md: 'none' },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: { xs: 'center', md: 'flex-start' },
                        textAlign: { xs: 'center', md: 'left' },
                        position: 'relative'
                    }}>
                        <Typography sx={{ color: '#737373', fontWeight: 600, fontSize: '1rem', mb: 0.5 }}>
                            {providerData?.name || 'Talib Ahmed'}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 3 }}>
                            {providerData?.title || '30 Minute Meeting'}
                        </Typography>

                        <Stack spacing={2} sx={{ width: '100%', alignItems: { xs: 'center', md: 'flex-start' } }}>
                            <Box display="flex" alignItems="center" gap={1.5} sx={{ color: '#737373', fontWeight: 500, fontSize: '0.95rem' }}>
                                <AccessTimeFilledIcon sx={{ color: '#737373', fontSize: '1.2rem' }} />
                                <span>{providerData?.duration || '30 min'}</span>
                            </Box>
                            <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ color: '#737373', fontWeight: 500, fontSize: '0.95rem' }}>
                                <VideocamIcon sx={{ color: '#737373', fontSize: '1.2rem' }} />
                                <span>Web conferencing details provided upon confirmation.</span>
                            </Box>
                        </Stack>

                        <Box sx={{ flexGrow: 1 }} />
                        {/* <Button variant="text" sx={{ color: CALENDLY_BLUE, textTransform: 'none', px: 0, fontWeight: 600, mt: 4, justifySelf: 'flex-end', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                            Cookie settings
                        </Button> */}
                    </Box>

                    {/* Middle / Right - Calendar & Slots Container */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, position: 'relative' }}>
                        {/* Powered By Ribbon */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            overflow: 'hidden',
                            width: '100px',
                            height: '100px',
                            zIndex: 10
                        }}>
                            <Box sx={{
                                backgroundColor: '#666',
                                color: '#fff',
                                py: 0.5,
                                px: 4,
                                position: 'absolute',
                                top: '20px',
                                right: '-35px',
                                transform: 'rotate(45deg)',
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                textAlign: 'center',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}>
                                POWERED BY<br />JustTawk
                            </Box>
                        </Box>

                        {/* Calendar Column */}
                        <Box sx={{
                            flex: showSlots ? '1 1 60%' : '1',
                            p: { xs: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'flex-start',
                            bgcolor: '#fff'
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 600, color: '#0a2540', mb: 4 }}>
                                Select a Date & Time
                            </Typography>

                            <Calendar
                                onChange={handleDateChange}
                                value={date}
                                minDate={new Date()}
                                prev2Label={null}
                                next2Label={null}
                                view="month"
                                calendarType="iso8601"
                                className={styles["custom-calendar-view"]}
                                showNeighboringMonth={false}
                            />
                            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                                    Time zone
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }}>
                                    <PublicIcon fontSize="small" sx={{ color: '#1a1a1a' }} />
                                    <Typography variant="body2" sx={{ color: '#1a1a1a', '&:hover': { textDecoration: 'underline' } }}>
                                        {Intl.DateTimeFormat().resolvedOptions().timeZone.replace('_', ' ')} ({(new Date()).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}) ▼
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Slots Column */}
                        {showSlots && (
                            <Box className={`${styles["slots-container"]} custom-scrollbar ${styles["animate-slide-in"]}`} sx={{ flex: '1 1 40%', pt: 4 }}>
                                <div className={styles["slots-header"]}>
                                    <Typography variant="h6" className={styles["slots-header-title"]}>
                                        {date ? format(date, 'EEEE, MMMM d') : ''}
                                    </Typography>

                                    {showDashboard && (
                                        <Box>
                                            {!isBlockingMode ? (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    color="warning"
                                                    onClick={() => setIsBlockingMode(true)}
                                                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                                >
                                                    Block Slots
                                                </Button>
                                            ) : (
                                                <Box display="flex" gap={1}>
                                                    <Button
                                                        size="small"
                                                        color="inherit"
                                                        onClick={() => { setIsBlockingMode(false); setSelectedSlotsForBlock([]); }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="error"
                                                        disabled={selectedSlotsForBlock.length === 0 || blocking}
                                                        onClick={handleBlockSlots}
                                                        sx={{ boxShadow: 'none' }}
                                                    >
                                                        {blocking ? '...' : `Block (${selectedSlotsForBlock.length})`}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </div>

                                {loadingSlots ? (
                                    <Box display="flex" justifyContent="center" height="300px" alignItems="center" flexDirection="column" gap={2}>
                                        <CircularProgress size={30} thickness={4} sx={{ color: CALENDLY_BLUE }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>Finding availability...</Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', height: '100%', overflowY: 'auto', pr: 1 }}>
                                        {slots.length === 0 ? (
                                            <Box sx={{ p: 6, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: 4, border: '1px dashed #e5e7eb' }}>
                                                <Typography variant="body1" fontWeight={600} color="text.primary">
                                                    No times available.
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" mt={1}>
                                                    Please try selecting another date.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            slots.map((slot, i) => {
                                                const isSelectedForBlock = selectedSlotsForBlock.some(s => s.formattedStart === slot.formattedStart);
                                                // Convert 24hr to 12hr format if needed, but slots might already be formatted.
                                                let displayTime = slot.formattedStart;
                                                if (displayTime.includes(':')) {
                                                    let [h, m] = displayTime.split(':');
                                                    h = parseInt(h);
                                                    let ampm = h >= 12 ? 'pm' : 'am';
                                                    h = h % 12 || 12;
                                                    displayTime = `${h}:${m}${ampm}`;
                                                }
                                                return (
                                                    <Button
                                                        key={i}
                                                        fullWidth
                                                        onClick={() => handleSlotClick(slot)}
                                                        className={`${styles["slot-button"]} ${isSelectedForBlock ? styles["slot-button-blocked"] : ''}`}
                                                        sx={{
                                                            py: 1.5,
                                                            fontSize: '1rem',
                                                            borderRadius: 1,
                                                            textTransform: 'none',
                                                            border: '1px solid rgba(0, 105, 255, 0.4)',
                                                            color: CALENDLY_BLUE,
                                                            bgcolor: '#fff',
                                                            fontWeight: 600,
                                                            transition: 'all 0.2s',
                                                            '&:hover': {
                                                                border: `2px solid ${CALENDLY_BLUE}`,
                                                                bgcolor: '#fff',
                                                                py: '14px', // Adjust padding to avoid layout shift due to border-width change
                                                            }
                                                        }}
                                                    >
                                                        {displayTime}
                                                    </Button>
                                                )
                                            })
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </div>

            {/* Dashboard: Upcoming Meetings Section */}
            {showDashboard && (
                // <Paper elevation={0} sx={{ width: '100%', mt: 2, p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                //     <Typography variant="h6" gutterBottom fontWeight={700} color={TEXT_PRIMARY}>Upcoming Meetings</Typography>
                //     {loadingAppointments ? (
                //         <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
                //     ) : appointments.length === 0 ? (
                //         <Typography color="text.secondary">No upcoming meetings found.</Typography>
                //     ) : (
                //         <Grid container spacing={2}>
                //             {appointments.slice(0, 5).map((appt) => (
                //                 <Grid item xs={12} md={6} lg={4} key={appt._id}>
                //                     <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                //                         <Typography variant="subtitle1" fontWeight="bold">
                //                             {appt.guestDetails?.name || 'Guest'}
                //                         </Typography>
                //                         <Typography variant="body2" color="text.secondary">
                //                             {format(new Date(appt.startTime), 'PPP p')}
                //                         </Typography>
                //                         <Typography variant="body2" sx={{ mb: 1, mt: 1 }}>
                //                             {appt.purpose || 'No purpose specified'}
                //                         </Typography>
                //                         <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                //                             <Button size="small" variant="outlined" color="error" onClick={() => handleCancel(appt._id)}>
                //                                 Cancel
                //                             </Button>
                //                             <Button size="small" variant="outlined" onClick={() => handleRemind(appt._id)}>
                //                                 Remind
                //                             </Button>
                //                         </Box>
                //                     </Paper>
                //                 </Grid>
                //             ))}
                //         </Grid>
                //     )}
                // </Paper>
                <></>
            )}

            {/* Booking Form Modal */}
            <Dialog
                open={openForm}
                onClose={() => setOpenForm(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.5rem', pb: 1 }}>Enter Details</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5faff', borderRadius: 2, borderLeft: '4px solid #0069ff' }}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700}>
                            {providerData?.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {date && format(date, 'EEEE, MMMM do, yyyy')} • {selectedSlot?.formattedStart} • {providerData?.duration}
                        </Typography>
                    </Box>

                    <Stack spacing={2.5}>
                        <TextField
                            label="Name" fullWidth required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            variant="outlined"
                        />
                        <TextField
                            label="Email" fullWidth required type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                        <TextField
                            label="Phone Number" fullWidth
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />

                        {/* Guest Section */}
                        <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #eee' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1.5, color: 'text.secondary', letterSpacing: '0.5px' }}>ADD GUESTS</Typography>
                            <Box display="flex" gap={1}>
                                <TextField
                                    size="small"
                                    placeholder="Guest Email"
                                    fullWidth
                                    value={formData.guestInput}
                                    onChange={(e) => setFormData({ ...formData, guestInput: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddGuest(); } }}
                                />
                                <Button variant="outlined" onClick={handleAddGuest} sx={{ minWidth: 80, textTransform: 'none' }}>Add</Button>
                            </Box>
                            {formData.guests.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                    {formData.guests.map(guest => (
                                        <Chip key={guest.email} label={guest.email} onDelete={() => handleRemoveGuest(guest.email)} size="small" />
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.primary', mb: 1 }}>MEETING MODE</FormLabel>
                            <RadioGroup
                                row
                                value={formData.mode}
                                onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                            >
                                <FormControlLabel value="online" control={<Radio size="small" />} label={<Typography variant="body2">Online </Typography>} />
                                <FormControlLabel value="offline" control={<Radio size="small" />} label={<Typography variant="body2">In-Person</Typography>} />
                            </RadioGroup>
                        </FormControl>

                        {formData.mode === 'online' && (
                            <TextField
                                label="Meeting Link (Optional)"
                                placeholder="e.g. Google Meet link"
                                fullWidth
                                size="small"
                                value={formData.meetingLink}
                                onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>,
                                }}
                            />
                        )}

                        {formData.mode === 'offline' && (
                            <TextField
                                label="Address / Location (Optional)"
                                placeholder="Enter full address"
                                fullWidth
                                size="small"
                                value={formData.location}
                                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><LocationOnIcon fontSize="small" /></InputAdornment>,
                                }}
                            />
                        )}

                        <TextField
                            label="Additional Notes"
                            placeholder="Please share anything that will help prepare for our meeting."
                            fullWidth multiline rows={3}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={() => setOpenForm(false)} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button
                        onClick={handleBook}
                        variant="contained"
                        disabled={submitting || !formData.name || !formData.email}
                        sx={{ px: 4, py: 1.2, borderRadius: 2, fontWeight: 700, boxShadow: '0 4px 14px rgba(0,105,255,0.3)' }}
                    >
                        {submitting ? <CircularProgress size={24} color="inherit" /> : 'Confirm Booking'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BookingPage;
